/**
 * 振幅スペクトル 1 フレームから特徴量を取り出す共通処理。
 *
 * 特徴量の出どころは 2 つある。再生中の AnalyserNode（audioAnalyser）と、
 * 再生せずに先頭だけデコードするオフライン解析（offlineSpectrum）。
 * 分類のしきい値は両者で同じ意味でなければ意味がないので、正規化も含めて
 * ここに一本化してある。呼び出し側の責務は「線形振幅の配列を渡す」ことだけ。
 */

/** 帯域の境界（Hz） */
const BAND_LOW_HZ = [20, 200] as const;
const BAND_MID_HZ = [200, 2000] as const;
const BAND_HIGH_HZ = [2000, 9000] as const;
/** 発話のフォルマントが収まる帯域（電話帯域）。声の手がかり */
const SPEECH_BAND_HZ = [300, 3400] as const;
/** 超低域。キックやベースの有無 = 音楽の手がかり */
const SUB_BASS_HZ = [20, 120] as const;
/** 平坦度を測る帯域。両端は測定が不安定なので避ける */
const FLATNESS_BAND_HZ = [120, 8000] as const;
/**
 * 自動ゲインの追従。小音量のファイルでも動きが出るよう、観測した最大値で
 * 正規化する。急に上がり、ゆっくり下がる（音が止んだ瞬間に暴れないため）
 */
const AGC_DECAY_PER_SEC = 0.5;
/**
 * 自動ゲインの下限。
 *
 * 全ビンの平均振幅という尺度は思ったより小さい。エネルギーが数ビンに集まる
 * トーン的な音では、フルスケールの正弦波でも 1e-4 程度にしかならない
 * （ピークビンの ~0.25 を 1024 ビンで薄めた値）。下限をこれより上に置くと
 * 常に下限で頭打ちになり、正規化後のレベルが有音判定のしきい値に届かず
 * 全フレームが無音扱いになる。実データの桁より 1 桁下に置くこと。
 */
const AGC_FLOOR = 1e-5;
/** これ未満の平均振幅は無音とみなす（自動ゲインを追従させない） */
const SILENCE_LEVEL = 1e-6;

export interface SpectrumFrame {
  /** 全帯域の平均レベル（0-1、自動ゲイン後） */
  level: number;
  /** 低域 20-200Hz（0-1、自動ゲイン後） */
  low: number;
  /** 中域 200-2000Hz（0-1、自動ゲイン後） */
  mid: number;
  /** 高域 2000-9000Hz（0-1、自動ゲイン後） */
  high: number;
  /** 前フレームからのスペクトルの増加量（0-1）。オンセットの手がかり */
  flux: number;
  /** スペクトル平坦度（0-1）。1 に近いほどノイズ的 */
  flatness: number;
  /** 発話帯域のエネルギー比（0-1） */
  speechBand: number;
  /** 超低域のエネルギー比（0-1） */
  subBass: number;
}

export type FeatureExtractor = (_magnitude: Float32Array, _dtSec: number) => SpectrumFrame;

/** 無音フレーム。分類器はこれを統計に入れず、時間軸だけ進める */
export const SILENT_FRAME: Readonly<SpectrumFrame> = {
  level: 0,
  low: 0,
  mid: 0,
  high: 0,
  flux: 0,
  flatness: 0,
  speechBand: 0,
  subBass: 0,
};

/** Hz をビン番号に変換する */
const binOf = (hz: number, sampleRate: number, binCount: number): number =>
  Math.max(0, Math.min(binCount - 1, Math.round((hz / (sampleRate / 2)) * binCount)));

/**
 * 特徴量抽出器を作る。
 *
 * 自動ゲインと前フレームのスペクトルを内部に持つため、1 つの音源につき
 * 1 つ作り、フレームを時間順に渡すこと。
 *
 * @param sampleRate 元音声のサンプリング周波数
 * @param binCount 渡す振幅配列の長さ（FFT サイズの半分）
 */
export function createFeatureExtractor(sampleRate: number, binCount: number): FeatureExtractor {
  const range = (hz: readonly [number, number]): [number, number] => [
    binOf(hz[0], sampleRate, binCount),
    binOf(hz[1], sampleRate, binCount),
  ];
  const lowRange = range(BAND_LOW_HZ);
  const midRange = range(BAND_MID_HZ);
  const highRange = range(BAND_HIGH_HZ);
  const speechRange = range(SPEECH_BAND_HZ);
  const subBassRange = range(SUB_BASS_HZ);
  const flatnessRange = range(FLATNESS_BAND_HZ);

  const previous = new Float32Array(binCount);
  let agc = AGC_FLOOR;

  return (magnitude: Float32Array, dtSec: number): SpectrumFrame => {
    const sumOver = ([from, to]: [number, number]): number => {
      let sum = 0;
      for (let i = from; i <= to; i++) sum += magnitude[i];
      return sum;
    };
    const meanOver = (r: [number, number]): number =>
      r[1] >= r[0] ? sumOver(r) / (r[1] - r[0] + 1) : 0;

    let total = 0;
    let flux = 0;
    for (let i = 0; i < binCount; i++) {
      const m = magnitude[i];
      total += m;
      const delta = m - previous[i];
      if (delta > 0) flux += delta;
      previous[i] = m;
    }

    const rawLevel = total / binCount;
    if (rawLevel < SILENCE_LEVEL) return SILENT_FRAME;

    const dt = Math.min(0.5, Math.max(0.001, dtSec));
    agc = Math.max(rawLevel, AGC_FLOOR, agc * Math.pow(AGC_DECAY_PER_SEC, dt));
    const normalize = (v: number): number => Math.min(1, v / agc);

    // 平坦度は幾何平均 / 算術平均。トーン的なら小さく、ノイズ的なら 1 に近い
    let logSum = 0;
    let linearSum = 0;
    let count = 0;
    for (let i = flatnessRange[0]; i <= flatnessRange[1]; i++) {
      logSum += Math.log(magnitude[i] + 1e-12);
      linearSum += magnitude[i];
      count += 1;
    }
    const geometric = count > 0 ? Math.exp(logSum / count) : 0;
    const arithmetic = count > 0 ? linearSum / count : 0;

    return {
      level: normalize(rawLevel),
      low: normalize(meanOver(lowRange)),
      mid: normalize(meanOver(midRange)),
      high: normalize(meanOver(highRange)),
      flux: Math.min(1, flux / (agc * binCount * 0.35)),
      flatness: arithmetic > 0 ? Math.min(1, geometric / arithmetic) : 0,
      speechBand: sumOver(speechRange) / total,
      subBass: sumOver(subBassRange) / total,
    };
  };
}
